"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSuperDrawer = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const BeamContext_1 = require("../BeamContext");
const Modal_1 = require("../Modal");
const ConfirmCloseModal_1 = require("./ConfirmCloseModal");
function useSuperDrawer() {
    const { drawerContentStack: contentStack, modalState, drawerCanCloseChecks: canCloseChecks, drawerCanCloseDetailsChecks: canCloseDetailsChecks, } = (0, BeamContext_1.useBeamContext)();
    const { openModal } = (0, Modal_1.useModal)();
    function canCloseDrawerDetails(i, doChange) {
        var _a;
        for (const canCloseDrawerDetail of (_a = canCloseDetailsChecks.current[i]) !== null && _a !== void 0 ? _a : []) {
            if (!canClose(canCloseDrawerDetail)) {
                openModal({ content: (0, jsx_runtime_1.jsx)(ConfirmCloseModal_1.ConfirmCloseModal, { onClose: doChange, ...canCloseDrawerDetail }) });
                return false;
            }
        }
        return true;
    }
    /**
     * Validates whether a new Superdrawer can be opened / whether the current Superdrawer can be closed, performing an
     * action to "do on change"  when all validations (or close confirmations from the user) have passed.
     * Breaking the validation into a separate function allows openInDrawer
     * to validate the drawer's state without closing it
     * @param doChange - A lambda to run once all validations have passed (or once the user has confirmed that they want to close)
     * */
    function maybeChangeDrawer(doChange) {
        const contentStackLength = contentStack.current.length;
        if (contentStackLength === 0) {
            doChange();
            return;
        }
        // Attempt to close each drawer details
        for (let i = contentStackLength - 2; i >= 0; i--) {
            if (!canCloseDrawerDetails(i, doChange)) {
                return;
            }
        }
        // Attempt to close the drawer
        for (const canCloseDrawer of canCloseChecks.current) {
            if (!canClose(canCloseDrawer)) {
                openModal({
                    content: (0, jsx_runtime_1.jsx)(ConfirmCloseModal_1.ConfirmCloseModal, { onClose: doChange, ...canCloseDrawer }),
                });
                return;
            }
        }
        doChange();
    }
    // Separate close actions to reference then in actions
    const closeActions = (0, react_1.useMemo)(() => {
        return {
            /** Attempts to close the drawer. If any checks fail, a confirmation modal will appear */
            closeDrawer() {
                /** Reset the contentStack, all checks and modalState */
                function onClose() {
                    const first = contentStack.current[0];
                    if ((first === null || first === void 0 ? void 0 : first.kind) === "open" && first.opts.onClose) {
                        first.opts.onClose();
                    }
                    contentStack.current = [];
                    canCloseChecks.current = [];
                    canCloseDetailsChecks.current = [];
                }
                maybeChangeDrawer(onClose);
                return;
            },
            closeDrawerDetail() {
                if (contentStack.current.length < 2)
                    return;
                // Attempt to close the current drawer details
                if (!canCloseDrawerDetails(contentStack.current.length - 2, onClose)) {
                    return;
                }
                /** Pop an element from the contentStacks and details checks */
                function onClose() {
                    // Pop contentStack and the current canCloseDrawerDetailsCheck
                    contentStack.current = contentStack.current.slice(0, -1);
                    canCloseDetailsChecks.current = canCloseDetailsChecks.current.slice(0, -1);
                    // Reset Modal state
                    modalState.current = undefined;
                }
                onClose();
            },
        };
    }, [canCloseChecks, canCloseDetailsChecks, contentStack, modalState, openModal]);
    // useMemo the actions separately from the dynamic isDrawerOpen value
    const actions = (0, react_1.useMemo)(() => {
        return {
            // TODO: Maybe we should rename to openDrawer as a breaking change (to match openDrawerDetail)
            openInDrawer(opts) {
                // When opening a new SuperDrawer, check if we can close the previous
                // SuperDrawer content. Bail if it fails
                // TODO: There needs to be try again logic here...
                maybeChangeDrawer(() => {
                    contentStack.current = [{ kind: "open", opts }];
                });
            },
            openDrawerDetail(opts) {
                // TODO: Check modal open?
                if (!contentStack.current.length) {
                    throw new Error("openInDrawer was not called before openDrawerDetail");
                }
                contentStack.current = [...contentStack.current, { kind: "detail", opts }];
            },
            /** Add a new close check to SuperDrawer */
            addCanCloseDrawerCheck(canCloseCheck) {
                // Check if we can add a canCloseDrawer check
                const stackLength = contentStack.current.length;
                if (!stackLength) {
                    console.error("Cannot add canCloseDrawerCheck when the SuperDrawer is not open");
                    return;
                }
                canCloseChecks.current = [...canCloseChecks.current, canCloseCheck];
            },
            /** Add a new close check to the current SuperDrawer detail */
            addCanCloseDrawerDetailCheck(canCloseCheck) {
                var _a;
                // Check if we can add a canCloseDrawerDetailCheck
                const stackLength = contentStack.current.length;
                if (stackLength <= 1) {
                    console.error("Cannot add canCloseDrawerDetailCheck when no SuperDrawer details drawer is open");
                    return;
                }
                // Add canCloseDetails check to the current details content
                canCloseDetailsChecks.current[stackLength - 2] = [
                    ...((_a = canCloseDetailsChecks.current[stackLength - 2]) !== null && _a !== void 0 ? _a : []),
                    canCloseCheck,
                ];
            },
        };
    }, [canCloseChecks, canCloseDetailsChecks, closeActions, contentStack]);
    return {
        ...actions,
        ...closeActions,
        isDrawerOpen: contentStack.current.length > 0,
    };
}
exports.useSuperDrawer = useSuperDrawer;
function canClose(canCloseCheck) {
    return ((typeof canCloseCheck === "function" && canCloseCheck()) ||
        (typeof canCloseCheck !== "function" && canCloseCheck.check()));
}
